import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { participantShare } from '../format';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * Participant detail — one person on one purchased product.
 *
 * Fifteen fields and no actions, which is what the old page has too. Its file
 * *contains* a cancel dialog, but nothing ever opens it and the function it would
 * call, `participants.cancelParticipant`, does not exist in the api module — so
 * the button under Actions there says "None". Dead code is not a feature to port;
 * noted in REWRITE-STATE.md in case cancelling a participant is wanted, which
 * would be a backend question first.
 *
 * The Purchase link needs a second request: a participant knows its
 * `PurchaseProductId`, and the purchase id lives on the product. The old page
 * fetches it when you click, which means a button that looks like a link and
 * cannot be middle-clicked. Here it is fetched with the participant, so the panel
 * holds a real link.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const ParticipantInternal = ({ api }) => {
  const { participantId } = useParams();

  const [participant, setParticipant] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    let data = null;
    try {
      data = await api.participants.getParticipant(participantId);
      if (!data || data.success === false || !(data.Id || data.id)) {
        setFailed(true);
        return;
      }
      setParticipant(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
      return;
    }

    if (data.PurchaseProductId) {
      try {
        const product = await api.purchases.getPurchaseProduct(data.PurchaseProductId);
        setPurchaseId(product?.PurchaseId || null);
      } catch (error) {
        // The purchase link is simply absent; the rest of the page is fine.
        setPurchaseId(null);
      }
    }
  }, [api, participantId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/participants">← All participants</BackToList>
        <div className="next-note next-note--error">
          Could not load this participant.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!participant) return <div className="next-page next-muted">Loading…</div>;

  const facts = [
    ['Status', participant.Status
      ? <StatusPill status={participant.Status}>{participant.Status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Type', participant.Type || <span className="next-muted">—</span>],
    ['Invitee name', participant.InviteeName || <span className="next-muted">—</span>],
    // The payload sets this from InviteeName in the backend, so it repeats the
    // invitee — see REWRITE-STATE.md.
    ['Inviter name', participant.InviterName || <span className="next-muted">—</span>],
    ['Membership slots', participant.MembershipSlots ?? <span className="next-muted">—</span>],
    ['Participant index', participantShare(participant.ParticipantsIndex) ?? <span className="next-muted">—</span>],
    ['Reference', participant.Reference || <span className="next-muted">—</span>],
    ['Category', participant.Category || <span className="next-muted">—</span>],
    ['Created at (CET)', fmtDate(participant.CreatedAtCet)],
    ['Responded at', fmtDate(participant.RespondedAt)],
    ['Occurs at (CET)', fmtDate(participant.OccursAtCet)],
    ['Purchase product id', participant.PurchaseProductId
      ? <Guid value={participant.PurchaseProductId} />
      : <span className="next-muted">—</span>],
    ['User id', participant.UserId
      ? <Guid value={participant.UserId} to={`/next/user/${participant.UserId}`} />
      : <span className="next-muted">—</span>],
    ['Facility id', participant.FacilityId
      ? <Guid value={participant.FacilityId} href={`/facility/${participant.FacilityId}`} />
      : <span className="next-muted">—</span>],
    // `SubcriptionId` — the misspelling is the payload's, and reading the correct
    // spelling instead would silently show nothing.
    ['Subscription id', participant.SubcriptionId
      ? <Guid value={participant.SubcriptionId} to={`/next/subscription/${participant.SubcriptionId}`} />
      : <span className="next-muted">—</span>]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/participants">← All participants</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{participant.InviteeName || 'Participant'}</h1>
          {participant.Status && <StatusPill status={participant.Status}>{participant.Status}</StatusPill>}
        </div>
        <span className="next-detail__id">{participant.Id || participantId}</span>
      </header>

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Participant</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                purchaseId && {
                  label: 'Purchase',
                  hint: 'what was bought',
                  to: `/next/purchase/${purchaseId}`
                },
                participant.PurchaseProductId && {
                  label: 'Other participants',
                  hint: 'on the same product',
                  to: `/next/participants?purchaseProductId=${participant.PurchaseProductId}`
                },
                participant.UserId && {
                  label: 'User',
                  to: `/next/user/${participant.UserId}`
                },
                participant.UserId && {
                  label: 'This user’s participations',
                  to: `/next/participants?userId=${participant.UserId}`
                },
                participant.SubcriptionId && {
                  label: 'Subscription',
                  to: `/next/subscription/${participant.SubcriptionId}`
                },
                participant.FacilityId && {
                  label: 'Facility',
                  href: `/facility/${participant.FacilityId}`
                },
                participant.FacilityId && {
                  label: 'Participants at this facility',
                  to: `/next/participants?facilityId=${participant.FacilityId}`
                }
              ]}
            />
          </section>

          {/*
            No Actions panel. The old page has none either: its file carries a
            cancel dialog that nothing opens, calling an api function that does
            not exist.
          */}
        </aside>
      </div>
    </div>
  );
};

ParticipantInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Participant = withApi(ParticipantInternal);

Participant.requiresLogin = true;

export default Participant;
